/**
 * Chat message list with typing indicator
 */

import { Bot, User, CheckCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ChatMessage as ChatMessageType } from "@/pages/ImageDirectorChat/types";

const PROMPT_MARKER = "[MAIN SUBJECT & SCENE]";

interface ChatMessagesProps {
  messages: ChatMessageType[];
  isLoading: boolean;
}

export const ChatMessages = ({ messages, isLoading }: ChatMessagesProps) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-4">
      {messages.map((message, index) => {
        const isPromptMessage =
          message.role === "assistant" && message.content.includes(PROMPT_MARKER);

        return (
          <div
            key={index}
            className={cn(
              "flex gap-3",
              message.role === "user" ? "justify-end" : "justify-start"
            )}
          >
            {message.role === "assistant" && (
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  isPromptMessage ? "bg-green-500/10" : "bg-primary/10"
                )}
              >
                {isPromptMessage ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <Bot className="w-4 h-4 text-primary" />
                )}
              </div>
            )}
            <div
              className={cn(
                "max-w-[80%] rounded-2xl px-4 py-3",
                message.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : isPromptMessage
                    ? "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30"
                    : "bg-secondary text-foreground border border-border"
              )}
            >
              {message.role === "assistant" ? (
                isPromptMessage ? (
                  <p className="text-sm font-medium">{t.director.promptReady}</p>
                ) : (
                  <div className="prose prose-sm prose-neutral dark:prose-invert">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                )
              ) : (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
            </div>
            {message.role === "user" && (
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-primary-foreground" />
              </div>
            )}
          </div>
        );
      })}

      {/* Typing indicator */}
      {isLoading && (
        <div className="flex gap-3 justify-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-primary" />
          </div>
          <div className="bg-secondary border border-border rounded-2xl px-4 py-3">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
